/**
 * JsonViewer Component
 *
 * Renders JSON with syntax highlighting and collapsible sections.
 * Supports dark theme with Tailwind colors.
 */

import { Component, createSignal, For, Show } from 'solid-js';

interface JsonViewerProps {
  data: unknown;
  expanded?: boolean;
  maxDepth?: number;
  class?: string;
}

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

const JsonViewer: Component<JsonViewerProps> = (props) => {
  return (
    <div class={`font-mono text-xs overflow-x-auto ${props.class || ''}`}>
      <JsonNode
        value={props.data as JsonValue}
        depth={0}
        maxDepth={props.maxDepth ?? 10}
        initialExpanded={props.expanded ?? true}
      />
    </div>
  );
};

interface JsonNodeProps {
  value: JsonValue;
  depth: number;
  maxDepth: number;
  initialExpanded: boolean;
  keyName?: string;
}

const JsonNode: Component<JsonNodeProps> = (props) => {
  const [expanded, setExpanded] = createSignal(props.initialExpanded && props.depth < 2);

  const valueType = () => {
    if (props.value === null) return 'null';
    if (Array.isArray(props.value)) return 'array';
    return typeof props.value;
  };

  const isExpandable = () => {
    return (valueType() === 'object' || valueType() === 'array') && props.value !== null;
  };

  const renderPrimitive = () => {
    const type = valueType();

    switch (type) {
      case 'string':
        // Truncate very long strings
        const str = props.value as string;
        const truncated = str.length > 500 ? str.slice(0, 500) + '...' : str;
        return <span class="text-green-400">"{truncated}"</span>;
      case 'number':
        return <span class="text-orange-400">{String(props.value)}</span>;
      case 'boolean':
        return <span class="text-purple-400">{String(props.value)}</span>;
      case 'null':
        return <span class="text-gray-500">null</span>;
      default:
        return <span class="text-gray-400">{String(props.value)}</span>;
    }
  };

  const entries = () => {
    if (valueType() === 'array') {
      return (props.value as JsonValue[]).map((v, i) => ({ key: String(i), value: v }));
    }
    if (valueType() === 'object' && props.value !== null) {
      return Object.entries(props.value as Record<string, JsonValue>).map(([k, v]) => ({
        key: k,
        value: v,
      }));
    }
    return [];
  };

  const indent = () => 'pl-4';

  // Primitive values
  if (!isExpandable()) {
    return (
      <span class="inline">
        <Show when={props.keyName !== undefined}>
          <span class="text-cyan-400">"{props.keyName}"</span>
          <span class="text-gray-500">: </span>
        </Show>
        {renderPrimitive()}
      </span>
    );
  }

  // Object or Array
  const brackets = valueType() === 'array' ? ['[', ']'] : ['{', '}'];
  const entryList = entries();

  // Empty array/object
  if (entryList.length === 0) {
    return (
      <span class="inline">
        <Show when={props.keyName !== undefined}>
          <span class="text-cyan-400">"{props.keyName}"</span>
          <span class="text-gray-500">: </span>
        </Show>
        <span class="text-gray-400">{brackets[0]}{brackets[1]}</span>
      </span>
    );
  }

  // Collapsed preview
  if (!expanded()) {
    return (
      <span class="inline">
        <Show when={props.keyName !== undefined}>
          <span class="text-cyan-400">"{props.keyName}"</span>
          <span class="text-gray-500">: </span>
        </Show>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          class="text-gray-400 hover:text-gray-200 transition-colors"
        >
          {brackets[0]}
          <span class="text-gray-500">
            {valueType() === 'array' ? ` ${entryList.length} items ` : ` ${entryList.length} keys `}
          </span>
          {brackets[1]}
        </button>
      </span>
    );
  }

  // Expanded view
  return (
    <div class="inline">
      <Show when={props.keyName !== undefined}>
        <span class="text-cyan-400">"{props.keyName}"</span>
        <span class="text-gray-500">: </span>
      </Show>
      <button
        type="button"
        onClick={() => setExpanded(false)}
        class="text-gray-400 hover:text-gray-200 transition-colors"
      >
        {brackets[0]}
      </button>
      <div class={indent()}>
        <For each={entryList}>
          {(entry, index) => (
            <div>
              <JsonNode
                keyName={valueType() === 'object' ? entry.key : undefined}
                value={entry.value}
                depth={props.depth + 1}
                maxDepth={props.maxDepth}
                initialExpanded={props.depth + 1 < 2}
              />
              <Show when={index() < entryList.length - 1}>
                <span class="text-gray-500">,</span>
              </Show>
            </div>
          )}
        </For>
      </div>
      <span class="text-gray-400">{brackets[1]}</span>
    </div>
  );
};

export default JsonViewer;
